/*
 * Manga Sekai Shop — cart helpers (Kivora cart, adapted — SPEC §6).
 *
 * 1. "Complete your 3" row (snippets/kv-cart-progress.liquid, part 'complete'), in the cart drawer and on the cart
 *    page: one tap on "+ ADD" posts ONE /cart/add.js with the cart sections, re-renders the drawer the Dawn way
 *    (cartDrawer.renderContents) or the cart page sections, then publishes PUB_SUB_EVENTS.cartUpdate. A compact
 *    <select data-mss-c3-variant> picks the variant for 2–12-variant products.
 * 2. <kv-money data-convert>: re-expresses a shop-currency amount in the shopper's currency (used by
 *    snippets/kv-offer-box.liquid). Kept from Kivora; harmless when nothing uses it.
 *
 * The Kivora free-gift engine is REMOVED on this theme (SPEC §0, §6): nothing here ever adds, removes or changes a
 * cart line on its own. The only cart write is the shopper's own "+ ADD" tap.
 */
(() => {
  if (window.KvCart && window.KvCart.version >= 4) return;

  // Dawn's <cart-items>/<cart-drawer-items> skip events from source 'cart-items' instead of re-fetching a section;
  // this script has already rendered every cart section on the page from its own response. Other subscribers (the
  // pick tray, the product offer box) still receive the event.
  const PUBLISH_SOURCE = 'cart-items';
  const ORIGIN = 'mss-cart';

  /* ---------------------------------------------------------------- money (kv-money) */

  const currencyInfo = () => (window.Shopify && window.Shopify.currency) || {};

  function rate() {
    const r = parseFloat(currencyInfo().rate);
    return r > 0 ? r : 1;
  }

  function formatMoney(cents, currency, trim) {
    const amount = Math.max(0, cents) / 100;
    const whole = trim && Math.round(amount) === amount;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(whole ? 0 : 2)}`;
    }
  }

  class KvMoney extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-convert')) return;
      const cents = parseInt(this.dataset.cents, 10);
      if (Number.isNaN(cents)) return;
      this.textContent = formatMoney(
        Math.round(cents * rate()),
        currencyInfo().active || 'USD',
        this.hasAttribute('data-trim')
      );
      this.classList.add('is-ready');
    }
  }

  if (!customElements.get('kv-money')) customElements.define('kv-money', KvMoney);

  /* ---------------------------------------------------------------- cart I/O */

  const routes = () => window.routes || {};
  const addUrl = () => `${routes().cart_add_url || '/cart/add'}.js`;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || (data && data.status && Number(data.status) >= 400)) {
      const error = new Error((data && (data.description || data.message)) || `HTTP ${res.status}`);
      error.status = res.status;
      error.description = data && (data.description || data.message);
      throw error;
    }
    return data;
  }

  /* Every cart section on this page that must follow a cart change (max 5 per Section Rendering API call). */
  function sectionTargets() {
    const targets = [];

    const drawer = document.querySelector('cart-drawer');
    if (drawer && document.getElementById('CartDrawer')) {
      targets.push({
        section: 'cart-drawer',
        drawer: true,
        apply(html, itemCount) {
          const source = parse(html).querySelector('#CartDrawer');
          const destination = document.getElementById('CartDrawer');
          if (!source || !destination) return;
          destination.innerHTML = source.innerHTML;
          const overlay = document.getElementById('CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
          if (typeof itemCount === 'number') drawer.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      targets.push({
        section: 'cart-icon-bubble',
        drawer: true,
        apply(html) {
          const source = parse(html).querySelector('.shopify-section');
          if (source) bubble.innerHTML = source.innerHTML;
        },
      });
    }

    const pageItems = document.getElementById('main-cart-items');
    if (pageItems && pageItems.dataset.id) {
      targets.push({
        section: pageItems.dataset.id,
        apply(html, itemCount) {
          const source = parse(html).querySelector('.js-contents');
          const destination = pageItems.querySelector('.js-contents');
          if (source && destination) destination.innerHTML = source.innerHTML;
          const cartItems = document.querySelector('cart-items');
          if (cartItems && typeof itemCount === 'number') cartItems.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    const pageFooter = document.getElementById('main-cart-footer');
    if (pageFooter && pageFooter.dataset.id) {
      targets.push({
        section: pageFooter.dataset.id,
        apply(html, itemCount) {
          const source = parse(html).querySelector('.js-contents');
          const destination = pageFooter.querySelector('.js-contents');
          if (source && destination) destination.innerHTML = source.innerHTML;
          if (typeof itemCount === 'number') pageFooter.classList.toggle('is-empty', itemCount === 0);
        },
      });
    }

    return targets.slice(0, 5);
  }

  function render(sections, targets, itemCount) {
    if (!sections) return;
    targets.forEach((target) => {
      if (!sections[target.section]) return;
      try {
        target.apply(sections[target.section], itemCount);
      } catch (e) {
        console.warn('[kv-cart] render failed for', target.section, e);
      }
    });
  }

  function announce(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: PUBLISH_SOURCE, origin: ORIGIN, cartData });
    }
  }

  /* After a re-render the old button is gone: keep keyboard users inside the drawer, on the next "+ ADD". */
  function restoreFocus(inDrawer, scope) {
    const drawer = document.querySelector('cart-drawer');
    if (inDrawer && drawer && drawer.classList.contains('active') && typeof window.trapFocus === 'function') {
      const container = document.getElementById('CartDrawer') || drawer;
      const next =
        container.querySelector('[data-mss-c3-add]') ||
        container.querySelector('.drawer__inner') ||
        container.querySelector('.drawer__close');
      window.trapFocus(container, next);
      return;
    }
    const next = document.querySelector(`.mss-c3--${scope} [data-mss-c3-add]`) || document.getElementById('checkout');
    if (next) next.focus({ preventScroll: true });
  }

  /* ---------------------------------------------------------------- "Complete your 3" */

  const busy = new WeakSet();

  async function addFromRow(button) {
    if (busy.has(button)) return;
    const variantId = parseInt(button.dataset.variantId, 10);
    if (!variantId) return;

    const row = button.closest('.mss-c3__item');
    const error = row && row.querySelector('.mss-c3__error');
    const strip = button.closest('[data-mss-c3]');
    const scope = strip && strip.classList.contains('mss-c3--page') ? 'page' : 'drawer';
    const drawer = document.querySelector('cart-drawer');
    const inDrawer = Boolean(button.closest('cart-drawer'));
    const useDawnDrawer = inDrawer && drawer && typeof drawer.renderContents === 'function';

    const targets = sectionTargets();
    const ids = [];
    if (useDawnDrawer && typeof drawer.getSectionsToRender === 'function') {
      drawer.getSectionsToRender().forEach((section) => ids.push(section.id));
    }
    targets.forEach((target) => {
      if (!ids.includes(target.section)) ids.push(target.section);
    });

    busy.add(button);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      const response = await post(addUrl(), {
        items: [{ id: variantId, quantity: 1 }],
        sections: ids.slice(0, 5),
        sections_url: window.location.pathname,
      });

      if (useDawnDrawer) {
        // Dawn's own path (same as product-form.js): it replaces #CartDrawer + the cart bubble and keeps it open.
        drawer.renderContents(response);
        drawer.classList.remove('is-empty');
        render(
          response.sections,
          targets.filter((target) => !target.drawer),
          undefined
        );
      } else {
        render(response.sections, targets, undefined);
        if (drawer) drawer.classList.remove('is-empty');
      }

      announce(response);
      document.dispatchEvent(new CustomEvent('mss:cart-added', { detail: { variantId, source: 'complete-your-3' } }));
      setTimeout(() => restoreFocus(inDrawer, scope), 60);
    } catch (e) {
      busy.delete(button);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      if (error) {
        error.textContent =
          e.description ||
          (window.variantStrings && window.variantStrings.soldOut) ||
          (window.cartStrings && window.cartStrings.error) ||
          'Could not add this box. Please try again.';
        error.hidden = false;
      }
      console.warn('[kv-cart] add failed', e);
    }
  }

  function onClick(event) {
    const button = event.target.closest && event.target.closest('[data-mss-c3-add]');
    if (!button || button.tagName !== 'BUTTON') return;
    event.preventDefault();
    addFromRow(button);
  }

  /* Capture phase on document: the row sits inside Dawn's <cart-items>/<cart-drawer-items>, whose own 'change'
     listener would treat this select as a quantity input. Handle it here and stop it before it gets there. */
  function onChange(event) {
    const select = event.target.closest && event.target.closest('[data-mss-c3-variant]');
    if (!select) return;
    event.stopPropagation();
    const body = select.closest('.mss-c3__body');
    const button = body && body.querySelector('button[data-mss-c3-add]');
    if (button) button.dataset.variantId = select.value;
  }

  // Delegated on document: survives every drawer / cart-page re-render.
  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange, true);

  window.KvCart = {
    // Kept for older callers (the Kivora free-gift check). The gift engine is removed: this never touches the cart.
    check() {},
    version: 4,
  };
})();
